// REFACTORED: 2026-02-02
// FROM src/components/layout/DashboardLayout.tsx

// ============================================================================
// PAGE NAME NORMALIZATION & MATCHING UTILITIES
// ============================================================================

/**
 * Normalize page name for matching (handle case variations, spaces, underscores, hyphens)
 * Example: "Customer Sentiment" -> "customer-sentiment"
 */
export const normalizePageName = (pageName: string): string => {
  return pageName.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
};

/**
 * Check if a page is a DRI insights page (by checking normalized name or actual page name)
 */
export const isDriInsightsPage = (pageName: string): boolean => {
  const normalized = normalizePageName(pageName);
  return normalized === 'dri-insights' || 
         (normalized.includes('dri') && normalized.includes('insight')) ||
         (pageName.toLowerCase().includes('dri') && pageName.toLowerCase().includes('insight'));
};

/**
 * Check if a page is a Customer Sentiment page (by checking normalized name or actual page name)
 */
export const isCustomerSentimentPage = (pageName: string): boolean => {
  const normalized = normalizePageName(pageName);
  return normalized === 'customer-sentiment' ||
         normalized === 'customer-feedback-analytics' ||
         (normalized.includes('customer') && (normalized.includes('sentiment') || 
          (normalized.includes('feedback') && normalized.includes('analytics'))));
};

/**
 * Map a URL pathname to the canonical page slug used in Postgres `pages.name`.
 */
export const pathnameToPageSlug = (pathname: string): string => {
  if (pathname === '/' || pathname === '/dashboard') return 'dashboard';
  if (pathname === '/projects/portfolio') return 'projects-portfolio';
  if (pathname === '/sandbox-billing') return 'sandbox-billing';
  if (pathname === '/internal-project-request') return 'requests';
  if (pathname === '/my-files' || pathname === '/files-and-links') return 'important-links';
  if (pathname === '/customer-sentiment' || pathname === '/customer-feedback') {
    return 'customer-sentiment';
  }
  const segment = pathname.replace(/^\//, '').split('/')[0] || 'dashboard';
  return normalizePageName(segment);
};

/**
 * Find the page record for the current route using normalized name matching.
 */
export const findPageForPath = <T extends { name: string }>(
  pathname: string,
  pages: T[],
): T | undefined => {
  const slug = pathnameToPageSlug(pathname);

  const exact = pages.find((p) => p.name === slug);
  if (exact) return exact;

  const normalized = pages.find((p) => normalizePageName(p.name) === slug);
  if (normalized) return normalized;

  if (slug === 'customer-sentiment') {
    return pages.find((p) => isCustomerSentimentPage(p.name));
  }
  if (slug === 'dri-insights') {
    return pages.find((p) => isDriInsightsPage(p.name));
  }

  return undefined;
};

// ============================================================================
// ROUTE TO TAB MAPPING
// ============================================================================

interface Page {
  name: string;
  [key: string]: any;
}

interface RouteToTabParams {
  pathname: string;
  pages: Page[];
  authLoading: boolean;
  user: { role?: { name?: string } } | null;
  userHasAdminAccess: boolean;
  navigate: (path: string) => void;
}

/**
 * Map route pathname to active tab name
 * Handles all route-to-tab conversions and admin access redirects
 */
export const getActiveTabFromRoute = (params: RouteToTabParams): string => {
  const { pathname, pages, authLoading, user, userHasAdminAccess, navigate } = params;

  if (pathname === '/projects/executive-summary' || pathname.startsWith('/projects/executive-summary')) {
    return 'projects';
  }
  if (pathname === '/projects/portfolio') {
    const portfolioPage = pages.find((page) => {
      const normalized = page.name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
      return normalized === 'projects-portfolio';
    });
    return portfolioPage?.name || 'projects-portfolio';
  }
  if (pathname === '/ai-chatbot') {
    const aiPage = pages.find((page) => {
      const normalized = page.name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
      return normalized === 'ai-chatbot';
    });
    return aiPage?.name || 'ai-chatbot';
  }
  if (pathname === '/projects') {
    return 'projects';
  } else if (pathname === '/people-analytics') {
    return 'people-analytics';
  } else if (pathname === '/hr-stats') {
    // Resolve the actual page name from the backend page registry (handles
    // 'hr-stats', 'HR Stats', 'people-insights', etc.) so the layout's
    // currentPage lookup matches and we don't fall back to the dashboard
    // tab — that's what was making "Utilization Insights" show up as the
    // page title for People Insights.
    const hrPage = pages.find((p) => {
      const normalized = normalizePageName(p.name);
      return (
        normalized === 'hr-stats' ||
        normalized === 'people-insights' ||
        (normalized.includes('hr') && normalized.includes('stat'))
      );
    });
    return hrPage?.name || 'hr-stats';
  } else if (pathname === '/squad-structure') {
    return 'squad-structure';
  } else if (pathname === '/internal-project-request') {
    return 'requests';
  } else if (pathname === '/my-insights') {
    const insightsPage = pages.find((p) => normalizePageName(p.name) === 'my-insights');
    return insightsPage?.name || 'my-insights';
  } else if (pathname === '/my-team') {
    const teamPage = pages.find((p) => normalizePageName(p.name) === 'my-team');
    return teamPage?.name || 'my-team';
  } else if (pathname === '/user-feedback') {
    return 'user-feedback';
  } else if (pathname === '/customer-sentiment') {
    // Find the actual page name from database (might be 'customer-sentiment', 'Customer Sentiment', 'customer-feedback-analytics', etc.)
    const customerSentimentPage = pages.find(page => {
      const normalized = page.name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
      return normalized === 'customer-sentiment' || 
             normalized === 'customer-feedback-analytics' ||
             (normalized.includes('customer') && (normalized.includes('sentiment') || 
              (normalized.includes('feedback') && normalized.includes('analytics'))));
    });
    return customerSentimentPage?.name || 'customer-sentiment';
  } else if (pathname === '/my-files') {
    return 'important-links'; // Use the actual page name from database
  } else if (pathname === '/files-and-links') {
    return 'important-links';
  } else if (pathname === '/dri-insights') {
    // Find the actual page name from database (might be 'dri-insights', 'DRI Insights', etc.)
    const driPage = pages.find(page => {
      const normalized = page.name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
      return normalized === 'dri-insights' || 
             (normalized.includes('dri') && normalized.includes('insight'));
    });
    return driPage?.name || 'dri-insights';
  } else if (pathname === '/forms') {
    return 'forms';
  } else if (pathname === '/searce-assets') {
    return pages.find(p => p.name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-') === 'searce-assets')?.name || 'searce-assets';
  } else if (pathname === '/solution-story' || pathname === '/solution-stories/admin') {
    const storyPage = pages.find((p) => {
      const normalized = normalizePageName(p.name);
      return normalized === 'solution-story' || normalized === 'solution-stories';
    });
    return storyPage?.name || 'solution-story';
  } else if (pathname === '/admin') {
    if (!authLoading && user && !userHasAdminAccess) {
      navigate('/projects');
    }
    return 'admin';
  } else if (pathname === '/dashboard' || pathname === '/') {
    // Check if user has dashboard access before setting dashboard tab
    return 'dashboard';
  }

  // Default fallback
  return 'dashboard';
};


